import { Component, computed, OnDestroy, Signal, inject, TemplateRef, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PlaygroundService, PlaygroundRunInfo, EndpointCatalogService, NotificationService, SessionService, CikisiResourcesService } from '../../../core/services';
import { apiErrorMessage } from '../../../core/utils/api-error-message';
import { ModalService } from '../../../shared/modal';
import { openCikisiFilterSheet } from '../cikisi-filter-sheet/cikisi-filter-sheet';
import { isPhoneViewport } from '../../../core/utils/phone';
import { openHelpSheet } from '../help-sheet/help-sheet';
import { openDateSheet } from '../../../shared/date-sheet/date-sheet';
import { EndpointDefinition, getEndpointDefinition } from '../../../core/models';

import { AnswerResult } from '../models/answer-result.model';
import { buildCodeSnippets, CodeLanguage } from '../utils/code-snippet';
import { estimateEndpointPrice, PriceEstimate } from '../utils/endpoint-pricing';
import { PlaygroundQueryPage } from '../utils/playground-query-page';
import { buildCikisiFields } from '../utils/cikisi-fields';
import { PromptOptimizer } from '../prompt-optimizer/prompt-optimizer';
import {
  MilaMultiSelectFetcher,
  MilaMultiSelectOption,
} from '../../../shared/mila-multi-select/mila-multi-select-option.model';
import { PlaygroundKeyService } from '../utils/playground-key.service';

export interface FilterRowMeta {
  id: string;
  label: string;
  placeholder: string;
  /** Real backend param name for this row, exactly as the endpoint catalog
   *  (GET /endpoints) names it used only to look up its surcharge_cents
   *  for pricing kept independent of payloadField, the wire key
   *  buildPayload() actually sends. */
  pricingParam: string;
  payloadField: string;
  kind: 'text' | 'flat';
}

export type AnswerMode = 'direct' | 'reasoning';
export type AnswerOutputType = 'unstructured' | 'structured';

export interface CalState {
  open: boolean;
  year: number;
  month: number;
  selected: Date | null;
}

@Component({
  selector: 'app-answer',
  standalone: false,
  templateUrl: './answer.html',
  styleUrl: './answer.scss',
})
export class Answer implements OnDestroy, PlaygroundQueryPage {
  constructor(
    private fb: FormBuilder,
    private playgroundService: PlaygroundService,
    private catalogService: EndpointCatalogService,
    private notificationService: NotificationService,
    private lookupService: CikisiResourcesService,
    private sessionService: SessionService,
    private modalService: ModalService,
  ) {
    this.isCikisiUser = this.sessionService.organization?.isCikisiUser ?? false;

    this.form = this.buildForm();

    this.endpointDef = computed(() => getEndpointDefinition(this.catalogService.catalog(), 'answer'));


    // One fetcher per 'flat'/'hierarchy' row id see FILTER_ROW_META and
    // CikisiResourcesService for what each actually hits.
    this.flatFetchers = {
      'a-tags': this.lookupService.searchTags,
      'a-sc': this.lookupService.searchSourceCategories,
      'a-ss': this.lookupService.searchSavedSearches,
      'a-si': this.lookupService.searchSourceIds,
      'a-kt': this.lookupService.searchKnowledgeTopics,
      'a-th': this.lookupService.searchThesaurusClasses,
    };
    this.languageFetcher = this.lookupService.searchLanguages;
  }

  readonly flatFetchers: Record<string, MilaMultiSelectFetcher>;
  /** Backs the "Language search" field the static ISO 639-1 list, see CikisiResourcesService. */
  readonly languageFetcher: MilaMultiSelectFetcher;

  /** Gates the whole "Search only Cikisi content" section see answer.html. */
  readonly isCikisiUser: boolean;

  private readonly _destroy$ = new Subject<void>();

  readonly endpointDef: Signal<EndpointDefinition | undefined>;

  isLoading = false;
  status: 'idle' | 'ok' | 'error' = 'idle';
  response: AnswerResult | null = null;
  latencyMs: number | null = null;
  /** Real credits consumed by the last run, as reported by the backend not an estimate. */
  credits: number | null = null;
  runInfo: PlaygroundRunInfo | null = null;
  errorMessage: string | null = null;
  /** `structured` when the run in flight asked for claims, so the
   *  skeleton draws them; empty for prose only. */
  loadingFields: ReadonlySet<string> = new Set();
  loadingLabel = 'Mila is preparing your answer';
  loadingHint = '';
  loadingQuery = '';
  readonly resultView = 'answer' as const;
  readonly emptyHint = 'The result will include the answer and the articles it was written from.';

  activeLanguage: CodeLanguage = 'ruby';
  copied = false;

  // ── Collapsible sections Response starts collapsed, it's set once and
  // rarely revisited; the other two are what changes query to query. ──
  sectionOpen: Record<'question' | 'request' | 'response', boolean> = {
    question: true,
    request: true,
    response: false,
  };

  toggleSection(key: keyof Answer['sectionOpen']): void {
    this.sectionOpen[key] = !this.sectionOpen[key];
  }

  /** The key runs bill to: picked once in the run bar (playground.html). */
  private readonly apiKey = inject(PlaygroundKeyService);

  // Same content-filter rows, same param names, as /rag and /search except
  // 'topics' for Knowledge Topics, which is /answer's own confirmed field
  // name (not 'knowledge_topics'). The other five payloadFields mirror
  // /rag's naming as the best available reference; not independently
  // confirmed for /answer yet.
  //
  // pricingParam is separate from that: GET /endpoints' real catalog lists
  // /answer's Knowledge Topics param as `knowledge_topics`, same as /rag
  // it's used here for pricing only, and doesn't change payloadField/what
  // buildPayload() actually sends.
  readonly FILTER_ROW_META: FilterRowMeta[] = [
    // Its picker is disabled while the separate "Knowledge Base" checkbox
    // (see toggleKnowledgeBase()) is on searching the whole knowledge
    // base makes picking specific topics moot.
    { id: 'a-kt',   label: 'Knowledge Topics',    placeholder: 'Search knowledge topics…', pricingParam: 'knowledge_topics',   payloadField: 'topics',            kind: 'flat' },
    { id: 'a-tags', label: 'Tags',                placeholder: 'Search tags…',             pricingParam: 'tags',               payloadField: 'tags_ids',              kind: 'flat' },
    { id: 'a-sc',   label: 'Source categories',   placeholder: 'Search categories…',       pricingParam: 'source_categories',  payloadField: 'source_categories_ids', kind: 'flat' },
    { id: 'a-th',   label: 'Thesaurus / classes', placeholder: 'Search classes…',          pricingParam: 'thesaurus_classes',  payloadField: 'thesaurus_classes_ids', kind: 'flat' },
    { id: 'a-ss',   label: 'Saved searches',      placeholder: 'Search saved searches…',   pricingParam: 'saved_searches',     payloadField: 'saved_searches_ids',    kind: 'flat' },
    { id: 'a-si',   label: 'Source IDs',          placeholder: 'Search source IDs…',       pricingParam: 'source_ids',         payloadField: 'source_ids',        kind: 'flat' },
  ];

  readonly MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  readonly DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  form: FormGroup;

  private buildForm(): FormGroup {
    return this.fb.group({
      question: ['', Validators.required],
      mode: ['direct' as AnswerMode],
      outputType: ['unstructured' as AnswerOutputType],
      cikisiOnly: [this.isCikisiUser],
      // Its own field, a sibling of cikisiOnly rather than folded into
      // the Knowledge Topics row see buildCikisiFields()/
      // toggleKnowledgeBase().
      knowledgeBase: [false],
      languageSearch: this.fb.control<MilaMultiSelectOption[]>([]),
      filterRows: this.fb.array(this.FILTER_ROW_META.map(() => this.buildFilterRowGroup())),
      includeChips: this.fb.array<string>([]),
      excludeChips: this.fb.array<string>([]),
    });
  }

  private buildFilterRowGroup(): FormGroup {
    return this.fb.group({
      expanded: [false],
      chips: this.fb.control<MilaMultiSelectOption[]>([]),
    });
  }

  get filterRowsArray(): FormArray {
    return this.form.get('filterRows') as FormArray;
  }

  get includeChipsArray(): FormArray {
    return this.form.get('includeChips') as FormArray;
  }

  get excludeChipsArray(): FormArray {
    return this.form.get('excludeChips') as FormArray;
  }

  /** What MilaMultiSelect(Hierarchy) binds to on 'flat'/'hierarchy' rows. */
  rowSelectControlOf(rowCtrl: AbstractControl): FormControl<MilaMultiSelectOption[]> {
    return (rowCtrl as FormGroup).get('chips') as FormControl<MilaMultiSelectOption[]>;
  }

  /** Selected-count badge works for either row shape. */
  rowChipCount(rowCtrl: AbstractControl): number {
    return rowCtrl.value.chips?.length ?? 0;
  }

  // Deliberately doesn't clear chips on uncheck see buildCikisiFields().
  toggleRow(rowCtrl: AbstractControl): void {
    const expanded = (rowCtrl as FormGroup).get('expanded')!;
    expanded.setValue(!expanded.value);
  }

  /** Ignores the click entirely while Knowledge Base is on the Knowledge
   *  Topics row is disabled (see toggleKnowledgeBase()), so there's
   *  nothing to toggle. */
  onFilterRowHeaderClick(meta: FilterRowMeta, rowCtrl: AbstractControl): void {
    if (meta.id === 'a-kt' && this.form.value.knowledgeBase) return;
    // Phones pick in a bottom sheet instead of the inline picker; the row
    // turns on once something is picked.
    if (isPhoneViewport()) {
      openCikisiFilterSheet(this.modalService, meta, this.flatFetchers[meta.id], rowCtrl);
      return;
    }
    this.toggleRow(rowCtrl);
  }

  private get knowledgeTopicsRow(): FormGroup {
    const i = this.FILTER_ROW_META.findIndex(m => m.id === 'a-kt');
    return this.filterRowsArray.at(i) as FormGroup;
  }

  /** Checking this means "search the whole knowledge base" collapses
   *  and disables the Knowledge Topics row's own picker (picking specific
   *  topics doesn't mean anything once the whole thing is in scope), same
   *  as unchecking it re-enables that picker without restoring its
   *  expanded state or clearing whatever was already picked there. */
  toggleKnowledgeBase(): void {
    const control = this.form.get('knowledgeBase')!;
    const next = !control.value;
    control.setValue(next);

    const topicsRow = this.knowledgeTopicsRow;
    const chips = topicsRow.get('chips')!;
    if (next) {
      topicsRow.get('expanded')!.setValue(false);
      chips.disable();
    } else {
      chips.enable();
    }
  }

  setMode(mode: AnswerMode): void {
    this.form.get('mode')!.setValue(mode);
  }

  setOutputType(type: AnswerOutputType): void {
    this.form.get('outputType')!.setValue(type);
  }

  /** Shared Enter/comma-to-add, Backspace-to-pop-last handler for any chip FormArray. */
  private pushChipFromInput(event: KeyboardEvent, chips: FormArray): void {
    const input = event.target as HTMLInputElement;
    if ((event.key === 'Enter' || event.key === ',') && input.value.trim()) {
      event.preventDefault();
      const value = input.value.trim();
      const isDuplicate = (chips.value as string[]).some(v => v.toLowerCase() === value.toLowerCase());
      if (!isDuplicate) {
        chips.push(this.fb.control(value));
      }
      input.value = '';
    }
    if (event.key === 'Backspace' && !input.value && chips.length) {
      chips.removeAt(chips.length - 1);
    }
  }

  onDomainKey(event: KeyboardEvent, chips: FormArray): void {
    this.pushChipFromInput(event, chips);
  }

  removeDomainChip(chips: FormArray, i: number): void {
    chips.removeAt(i);
  }

  /** Opens "Perfect your prompt with our Prompt Optimizer" result is
   *  only applied to the question field if the user hits "Use this
   *  version" (see PromptOptimizer); undefined means cancelled. */
  openPromptOptimizer(): void {
    if (!this.form.value.question?.trim()) return; // belt-and-suspenders the trigger's own [disabled] already covers this
    this.modalService.medium(PromptOptimizer, {
      data: { question: this.form.value.question ?? '', endpoint: 'answer' },
    }).closed$.subscribe(result => {
      if (!result) return;
      this.form.patchValue({ question: result });
    });
  }

  /** Real backend param names currently in use, for surcharge lookup
   *  against the live catalog (see utils/endpoint-pricing.ts). */
  private get activeParamNames(): string[] {
    const v = this.form.value;
    const active: string[] = [];
    if (v.knowledgeBase) active.push('knowledge_base');
    this.FILTER_ROW_META.forEach((meta, i) => {
      const row = v.filterRows?.[i];
      if (row?.expanded && row.chips?.length) active.push(meta.pricingParam);
    });
    return active;
  }

  /** Live price estimate, recomputed from current form state on every
   *  change against the real catalog see utils/endpoint-pricing.ts. */
  get estimatedPrice(): PriceEstimate {
    return estimateEndpointPrice(this.endpointDef(), this.activeParamNames);
  }

  get requestSummary(): string {
    return (this.form.value.question ?? '').trim();
  }

  /** An example from the intro (utils/endpoint-intros.ts) into the question. */
  useExample(text: string): void {
    this.form.patchValue({ question: text });
  }

  get codeSnippet(): string {
    return buildCodeSnippets('answer', 'answer', this.buildPayload())[this.activeLanguage];
  }

  setLanguage(lang: CodeLanguage): void {
    this.activeLanguage = lang;
  }

  copySnippet(): void {
    navigator.clipboard?.writeText(this.codeSnippet).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  // ── Calendar Published between ────────────────────────────────────
  calFrom: CalState = this.newCal();
  calTo: CalState = this.newCal();

  private newCal(): CalState {
    const now = new Date();
    return { open: false, year: now.getFullYear(), month: now.getMonth(), selected: null };
  }

  toggleCal(cal: CalState, event: MouseEvent, title = 'Date'): void {
    event.stopPropagation();
    // Phones pick the date in a bottom sheet instead of the dropdown.
    if (isPhoneViewport()) {
      this.closeAll();
      openDateSheet(this.modalService, title, cal);
      return;
    }
    const wasOpen = cal.open;
    this.closeAll();
    if (!wasOpen) {
      cal.open = true;
    }
  }

  // ── Question tips panel (floats over the form, like Boolean operators) ──
  tipsOpen = false;

  @ViewChild('helpRows') private helpRows!: TemplateRef<unknown>;

  toggleTips(event: MouseEvent): void {
    event.stopPropagation();
    // Phones get the same rows as a bottom sheet instead of a popover.
    if (isPhoneViewport()) {
      this.closeAll();
      openHelpSheet(this.modalService, { title: 'Tips for using the direct mode', content: this.helpRows, panelClass: 'question-tips' });
      return;
    }
    const wasOpen = this.tipsOpen;
    this.closeAll();
    this.tipsOpen = !wasOpen;
  }

  closeTips(): void {
    this.tipsOpen = false;
  }

  /** Used from within toggleCal() to deliberately close
   *  whichever *other* dropdown was open when a new one is. Not for
   *  (appClickOutside) directly see closeCal(). */
  closeAll(): void {
    this.calFrom.open = false;
    this.calTo.open = false;
    this.tipsOpen = false;
  }

  /** Bound to (appClickOutside) on each calendar wrapper: closes just that
   *  calendar, not every dropdown on the page. ClickOutsideDirective fires
   *  on mousedown, before click. Wiring the wrappers to the blanket
   *  closeAll() means clicking a day inside a calendar would register as
   *  "outside" the other wrappers too: their listeners would fire on that
   *  same mousedown and tear the calendar's DOM out (via closeAll()) before
   *  the browser ever dispatches the click, so selectDay() would silently
   *  never run. Each wrapper closing only its own state avoids that. */
  closeCal(cal: CalState): void {
    cal.open = false;
  }

  prevMonth(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    if (cal.month === 0) { cal.month = 11; cal.year--; } else cal.month--;
  }

  nextMonth(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    if (cal.month === 11) { cal.month = 0; cal.year++; } else cal.month++;
  }

  calDays(cal: CalState): (number | null)[] {
    const first = new Date(cal.year, cal.month, 1).getDay();
    const count = new Date(cal.year, cal.month + 1, 0).getDate();
    const cells: (number | null)[] = Array(first).fill(null);
    for (let d = 1; d <= count; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    return cells;
  }

  selectDay(cal: CalState, day: number | null, event: MouseEvent): void {
    event.stopPropagation();
    if (!day) return;
    cal.selected = new Date(cal.year, cal.month, day);
    cal.open = false;
  }

  /** The × inside a date field that has a value. */
  clearDate(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    cal.selected = null;
    cal.open = false;
  }

  isSelected(cal: CalState, day: number | null): boolean {
    return !!(day && cal.selected &&
      cal.selected.getDate() === day &&
      cal.selected.getMonth() === cal.month &&
      cal.selected.getFullYear() === cal.year);
  }

  isToday(cal: CalState, day: number | null): boolean {
    if (!day) return false;
    const t = new Date();
    return t.getDate() === day && t.getMonth() === cal.month && t.getFullYear() === cal.year;
  }

  formatDate(cal: CalState): string {
    if (!cal.selected) return '';
    return cal.selected.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private isoDate(d: Date | null): string | null {
    return d ? d.toISOString().slice(0, 10) : null;
  }

  private buildPayload() {
    const v = this.form.value;

    // Nested the same way the UI is: `request` = the "Request configuration"
    // card, `response` = the "Response configuration" card mode/output_type
    // live there since they govern how the answer is generated and shaped,
    // not what gets searched. `question` sits inside `request`.
    return {
      request: {
        question: v.question,
        include_languages: (v.languageSearch as MilaMultiSelectOption[]).map(o => o.id),
        include_domains: v.includeChips,
        exclude_domains: v.excludeChips,
        published_from: this.isoDate(this.calFrom.selected),
        published_to: this.isoDate(this.calTo.selected),
        // {} entirely for a non-Cikisi org not cikisi_content_only/the
        // filter fields with empty values. See buildCikisiFields().
        ...buildCikisiFields(this.isCikisiUser, 'cikisi_content_only', v.cikisiOnly, v.knowledgeBase, this.FILTER_ROW_META, v.filterRows),
      },
      response: {
        mode: v.mode,
        output_type: v.outputType,
      },
    };
  }

  runQuery(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.error('Enter a question before running the query.');
      return;
    }

    // The Run button is disabled for this already (see playground.html) this is
    // just a backstop, since the backend can only 422 without a key to bill.
    if (!this.apiKey.keys().length) {
      this.notificationService.error('Create an API key before running this query.');
      return;
    }

    const payload = this.buildPayload();

    this.loadingFields = new Set(payload.response.output_type === 'structured' ? ['structured'] : []);
    // Worded from what this run asked for, not from progress the API doesn't report.
    const reasoning = payload.response.mode === 'reasoning';
    this.loadingLabel = reasoning ? 'Mila is reasoning through several searches' : 'Mila is preparing your answer';
    this.loadingHint = reasoning ? 'reasoning takes longer than direct' : '';
    this.loadingQuery = (payload.request.question ?? '').trim();
    this.isLoading = true;
    this.errorMessage = null;
    this.playgroundService.runAnswer(payload, this.apiKey.selectedId() ?? undefined)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.status = 'error';
          this.response = null;
          this.runInfo = null;
          // A failed query (body.success false) arrives as an Error carrying the
          // API's messages; a transport failure as an HttpErrorResponse.
          this.errorMessage = error instanceof HttpErrorResponse
            ? apiErrorMessage(error, 'The request failed. Please try again later.')
            : error?.message || 'The query did not succeed.';
          this.notificationService.error('An error occurred while running the query. Please try again later.');
          return throwError(error);
        }),
        tap(({ result, latencyMs, credits, run }) => {
          this.response = result;
          this.latencyMs = latencyMs;
          this.credits = credits;
          this.runInfo = run;
          this.status = 'ok';
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** Called from the shared Playground footer's Reset button (see
   *  playground.ts/.html) once the user confirms the confirmation
   *  prompt itself lives there, not here, since it's one shared footer
   *  for all seven playground pages. */
  resetForm(): void {
    this.form = this.buildForm();
    this.status = 'idle';
    this.response = null;
    this.latencyMs = null;
    this.credits = null;
    this.runInfo = null;
    this.errorMessage = null;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
